import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Container, Typography, Box, CircularProgress } from '@mui/material';
import { API_URL } from '../../utils/config';
import { getImageSrc } from '../../utils/imageHelper';
import { COLORS, FONTS, HOVER_SCALE_IMG_SX } from '../../theme/tokens';
import { getLocalizedContent } from '../../utils/localizedContent';
import { getActiveLanguage } from '../../i18n';

const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
};

const getExcerpt = (blog) => {
  const lang = getActiveLanguage();
  return stripHtml(
    getLocalizedContent(blog.description || blog.excerpt || blog.summary || blog.short_description || '', lang)
  );
};

const getReadTimeMinutes = (blog) => {
  const lang = getActiveLanguage();
  const text = stripHtml(
    getLocalizedContent(blog.blog_text || blog.description || '', lang)
  );
  const wordCount = text ? text.split(/\s+/).filter(Boolean).length : 0;
  return Math.max(1, Math.round(wordCount / 200));
};

const getBlogTitle = (blog) => getLocalizedContent(blog.title, getActiveLanguage());

const getCategory = (blog, fallback) => blog.category || fallback;

const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    if (typeof dateString === 'string' && dateString.indexOf(' ') > 0) {
      return dateString;
    }
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch (e) {
    return dateString;
  }
};

const buildMetaLine = (blog, t) => {
  const parts = [
    formatDate(blog.date || blog.created_at),
    blog.author || null,
    t('blog.readTime', { count: getReadTimeMinutes(blog) }),
  ].filter(Boolean);
  return parts.join(' · ');
};

const getBlogImageSrc = (image) => {
  if (!image) return null;
  return image.includes('://') ? image : getImageSrc(image, 'thumbs');
};

const BlogImage = ({ blog, objectFit = 'cover' }) => {
  if (!blog.image) return null;

  return (
    <Box
      sx={{
        aspectRatio: '3/2',
        overflow: 'hidden',
        bgcolor: COLORS.surfacePanel,
        ...HOVER_SCALE_IMG_SX,
      }}
    >
      <img
        src={getBlogImageSrc(blog.image)}
        alt={getBlogTitle(blog)}
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = `https://storage.googleapis.com/eladore-ruffles.appspot.com/images/thumbs/${blog.image}`;
        }}
        style={{
          width: '100%',
          height: '100%',
          objectFit,
          objectPosition: 'center',
          display: 'block',
        }}
      />
    </Box>
  );
};

const FeaturedCategoryTag = ({ label }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      bgcolor: COLORS.green,
      color: '#FFFFFF',
      borderRadius: '2px',
      px: '12px',
      py: '4px',
      fontSize: '0.6875rem',
      fontWeight: 700,
      letterSpacing: '1.2px',
      textTransform: 'uppercase',
      fontFamily: FONTS.body,
    }}
  >
    {label}
  </Box>
);

const GridCategoryTag = ({ label }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      color: COLORS.goldText,
      border: `1px solid ${COLORS.gold}`,
      borderRadius: '2px',
      px: '12px',
      py: '4px',
      fontSize: '0.6875rem',
      fontWeight: 700,
      letterSpacing: '1.2px',
      textTransform: 'uppercase',
      fontFamily: FONTS.body,
    }}
  >
    {label}
  </Box>
);

const FeaturedPostCard = ({ blog }) => {
  const { t } = useTranslation();

  return (
  <Box
    data-testid="blog-featured-card"
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' },
      border: `1px solid ${COLORS.borderHairline}`,
      borderRadius: '2px',
      overflow: 'hidden',
      mb: { xs: 4, md: 6 },
    }}
  >
    <Link to={`/blog/${blog.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
      <BlogImage blog={blog} />
    </Link>
    <Box
      sx={{
        bgcolor: COLORS.surfacePanel,
        p: { xs: '32px', md: '48px' },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 2,
      }}
    >
      <FeaturedCategoryTag label={getCategory(blog, t('blog.category'))} />
      <Typography
        component={Link}
        to={`/blog/${blog.slug}`}
        sx={{
          fontFamily: FONTS.heading,
          fontWeight: 600,
          fontSize: '1.75rem',
          color: COLORS.textPrimary,
          lineHeight: 1.2,
          textDecoration: 'none',
          '&:hover': {
            color: COLORS.green,
          },
        }}
      >
        {getBlogTitle(blog)}
      </Typography>
      {getExcerpt(blog) && (
        <Typography
          sx={{
            fontSize: '0.9375rem',
            color: COLORS.textBody,
            fontFamily: FONTS.body,
            lineHeight: 1.6,
          }}
        >
          {getExcerpt(blog)}
        </Typography>
      )}
      <Typography
        sx={{
          fontSize: '0.8125rem',
          color: COLORS.textFaint,
          fontFamily: FONTS.body,
        }}
      >
        {buildMetaLine(blog, t)}
      </Typography>
      <Typography
        component={Link}
        to={`/blog/${blog.slug}`}
        sx={{
          fontFamily: FONTS.body,
          fontSize: '0.875rem',
          fontWeight: 600,
          color: COLORS.green,
          textDecoration: 'none',
          '&:hover': {
            color: COLORS.greenDark,
          },
        }}
      >
        {t('blog.readStory')}
      </Typography>
    </Box>
  </Box>
  );
};

const GridPostCard = ({ blog }) => {
  const { t } = useTranslation();

  return (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    <Link to={`/blog/${blog.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
      <Box sx={{ borderRadius: '2px', overflow: 'hidden' }}>
        <BlogImage blog={blog} />
      </Box>
    </Link>
    <GridCategoryTag label={getCategory(blog, t('blog.category'))} />
    <Typography
      component={Link}
      to={`/blog/${blog.slug}`}
      sx={{
        fontFamily: FONTS.heading,
        fontWeight: 600,
        fontSize: '1.25rem',
        color: COLORS.textPrimary,
        textDecoration: 'none',
        lineHeight: 1.3,
        '&:hover': {
          color: COLORS.green,
        },
      }}
    >
      {getBlogTitle(blog)}
    </Typography>
    {getExcerpt(blog) && (
      <Typography
        sx={{
          fontSize: '0.875rem',
          color: COLORS.textSecondary,
          fontFamily: FONTS.body,
          lineHeight: 1.6,
        }}
      >
        {getExcerpt(blog)}
      </Typography>
    )}
    <Typography
      sx={{
        fontSize: '0.75rem',
        color: COLORS.textFaint,
        fontFamily: FONTS.body,
      }}
    >
      {buildMetaLine(blog, t)}
    </Typography>
  </Box>
  );
};

const BlogList = () => {
  const { t } = useTranslation();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/blogs`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || t('blog.loadFailed'));
      }
      const data = await response.json();
      setBlogs(data);
    } catch (fetchError) {
      setError(fetchError.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, color: 'error.main' }}>
        Error: {error}
      </Box>
    );
  }

  if (blogs.length === 0) {
    return (
      <Container>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          {t('blog.noPosts')}
        </Box>
      </Container>
    );
  }

  const featuredPost = blogs[0];
  const gridPosts = blogs.slice(1);

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
      <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
        <Typography
          sx={{
            color: COLORS.goldText,
            fontSize: '0.75rem',
            fontWeight: 700,
            letterSpacing: '2.5px',
            textTransform: 'uppercase',
            fontFamily: FONTS.body,
            mb: 1.5,
          }}
        >
          {t('blog.title')}
        </Typography>
        <Typography
          component="h1"
          sx={{
            fontFamily: FONTS.heading,
            fontWeight: 600,
            fontSize: '2.25rem',
            color: COLORS.textPrimary,
            mb: 1.5,
            lineHeight: 1.2,
          }}
        >
          {t('blog.tagline')}
        </Typography>
        <Typography
          sx={{
            fontSize: '0.9375rem',
            color: COLORS.textSecondary,
            fontFamily: FONTS.body,
            maxWidth: 480,
            mx: 'auto',
            lineHeight: 1.6,
          }}
        >
          {t('blog.intro')}
        </Typography>
      </Box>

      {featuredPost && <FeaturedPostCard blog={featuredPost} />}

      {gridPosts.length > 0 && (
        <Box
          data-testid="blog-grid"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: '28px',
          }}
        >
          {gridPosts.map((blog) => (
            <GridPostCard key={blog.id} blog={blog} />
          ))}
        </Box>
      )}
    </Container>
  );
};

export default BlogList;
